import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from '../theme';
import { BentoCard, Spinner, ServedFace } from './shared';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { attributeValueStandIn } from '../services/categoryMeta';
import {
  listSenderOrganisations, createSenderOrganisation, updateSenderOrganisation,
  confirmSenderOrganisation, rejectSenderOrganisation, mergeSenderOrganisation,
  deleteSenderOrganisation, SenderOrganisationRow, SenderOrganisationUpsert, SenderMatcher,
} from '../services/api';
import { formatBackendError, formatError } from '../utils/formatError';

// <purpose>Absender tab (P2 sender-identity registry, Task R3) — the admin review table for the
// registry's write-model. R1/R2 already render the registry's READ side on ticket rows and the
// ticket-list filter (SenderOrganisationRef — SenderPill on a list row, its hover carrying the
// address; the detail header states only the plain address, part 4c follow-up, 2026-09-29;
// SenderOrganisationFilterSection in the filter panel); this
// is where an admin reviews a PROPOSED match (confirm/merge/reject) and manages the roster
// (create/edit/delete) directly — the Kurzname included (migration 086, ruling R33, 2026-09-19):
// the short face the ticket list's partner pill wears, which is OPTIONAL by design, because a
// proposed row has none until it is reviewed and the pill falls back to the full name meanwhile.</purpose>
// <dependencies>GET/POST/PATCH/DELETE /manage/senders + the confirm/reject/merge sub-routes
// (services/api.ts). useCategoryMeta().attribute('sender_kind')/.attributeFace for the „Art" column
// and the form's kind <select> — never a hardcoded kind→label map (the tab itself is gated on this
// same attribute existing at all, see EinstellungenSeite's availableWhen).
// useCategoryMeta().face('registry_status', status) for the Status column's word (P4b) — a served
// fact, never a code-owned map.</dependencies>
// <constraints>Mirrors RoutingVerwaltung's layout-stability discipline: the thead/column-frame
// renders unconditionally, only tbody rows swap; the form lives in its own section below the table.
// Proposed rows sort first. The Aktionen column is a FIXED ACTIONS_COLUMN_WIDTH, whiteSpace:nowrap
// cell on every row regardless of status — a proposed row's three review actions and a
// seeded/confirmed/rejected row's two management actions both sit in one non-wrapping flex row
// inside that same fixed cell, so the row's own height (padding + single line of buttons) never
// changes between statuses. The table sits inside a horizontally scrolling wrapper (final review —
// a narrow card scrolls the table instead of the browser squeezing every column below its floor,
// which is how the Aktionen column got clipped in the first place). `kind === 'intern'` is the one
// reserved, non-tenant-vocabulary value the backend itself protects (DELETE 409
// intern_row_protected) — Löschen is disabled ahead of that call, not only after it fails.</constraints>

const thStyle: React.CSSProperties = { padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500, textAlign: 'left' };
// `overflowWrap` belongs WITH the fixed table layout above: a fixed column can't grow for a long
// matcher list or organisation name, so the text wraps inside its own cell instead of spilling
// across the neighbouring one.
// Fix loop pass 3: `anywhere` breaks a word at an arbitrary character even when the line has a
// perfectly good earlier wrap point (a space, or — the case that bit the eyeball — a hyphen inside
// a domain) if the browser judges it convenient; `break-word` breaks a word ONLY as the last resort,
// after normal wrapping (spaces, and the existing browser-standard break-after-hyphen rule) has
// already been tried. Verified directly (headless Chromium, real Poppins, real container chain, both
// 1440 and 1280): with `break-word`, a hyphenated domain/e-mail matcher
// ("stadtwerke-musterstadt.de", "kontakt@stadtwerke-musterstadt.de") still wraps at its own hyphen —
// identical rendering to `anywhere` — and a matcher with NO hyphen at all
// ("musterstadtenergieversorgungnord.de") still gets force-broken mid-token when the column is
// narrower than it, because `break-word`'s last-resort still fires once there is truly no other
// break opportunity — again pixel-identical to `anywhere`. No cell in this table needed `anywhere`;
// every ordinary word (organisation name, Kurzname, sender-kind face, header) that fits on its own
// line now stays on one line instead of breaking mid-word the moment the line is merely tight.
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13, overflowWrap: 'break-word' };
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 4, display: 'block',
};
function btnStyle(kind: 'primary' | 'danger' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500,
  };
  if (kind === 'primary') return { ...base, background: theme.colors.navy, color: theme.colors.white };
  if (kind === 'danger') return { ...base, background: theme.colors.white, color: theme.colors.error, border: '1px solid #e0b4b4' };
  return { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}
// ── Column widths (final review — the Aktionen column clipped its third button) ─────────────────
// The Aktionen column's width, sized to the widest action set this table ever shows: a proposed
// row's three buttons (Bestätigen/Zusammenführen/Ablehnen). Per btnStyle above, every button carries
// 14px padding on each side plus a 1px transparent border — content-box (no boxSizing override), so
// both add to the rendered width — and the row wrapping them has a 4px gap.
// Fix loop pass 3: the pass-1 label widths above were a canvas TEXT-ONLY estimate ("Zusammenführen"
// ≈118px) that turned out inflated — re-measured directly in headless Chromium (build 1243, real
// Poppins, the real container chain, real btnStyle) on the REAL rendered buttons, not text alone:
//   "Bestätigen"      button ≈ 91.4px
//   "Zusammenführen"  button ≈131.9px
//   "Ablehnen"        button ≈ 85.0px
//   row (left edge of Bestätigen to right edge of Ablehnen, i.e. buttons + 2×4px gap) ≈316.3px
// ACTIONS_COLUMN_WIDTH is applied as a `width` on the <th>/<td> — content-box, so it IS the space
// directly available to the flex row of buttons; the cell's own 24px horizontal padding (thStyle/
// tdStyle) is added by the browser ON TOP, never subtracted from this declared width (confirmed
// empirically — see TABLE_MIN_WIDTH's own comment for the declared-vs-actual arithmetic). Pass 3's
// FIRST cut (357 = ceil(row) + 24 + 16) double-counted that padding — adding it a second time here,
// as if the buttons had to compete with it for space — which is exactly what left the Aktionen cell
// with more visible empty space than intended (controller eyeball of the pass-3-first-cut render).
// Corrected: ACTIONS_COLUMN_WIDTH = ceil(that row) + a flat 16px margin for the case where index.css's
// fallback font stack (Poppins hasn't loaded yet) renders a label slightly wider than Poppins does —
// no separate padding term, because the real padding was never competing with the buttons to begin
// with: 317 + 16 = 333.
export const ACTIONS_COLUMN_WIDTH = 333;

// The header row's four Organisation/Kurzname/Art/Domains columns are PERCENTAGES (see the <th>
// widths below) — the constants below are NOT a per-column minWidth applied to any of them;
// `tableLayout: 'fixed'` has no such mechanism for a percentage column. The mechanism (headless
// Chromium build 1243, Poppins 500 13px, measured directly rather than assumed): the two FIXED-px
// columns (Status, Aktionen) are rendered at their declared width PLUS thStyle's 24px of horizontal
// padding each (content-box, no boxSizing override — the same reason ACTIONS_COLUMN_WIDTH's own
// comment above gives) — 104 -> 128px, 333 -> 357px actual. Whatever is LEFT of the table's own
// width after those two (TABLE_MIN_WIDTH - 128 - 357) is what the four percentage columns split
// BETWEEN THEMSELVES, in proportion to their own declared percentage — not against the table's full
// width.
//
// Fix loop pass 3 (controller eyeball of the pass-2 render): pass 2 sized these four floors to each
// header's OWN text alone and dropped the "does the CONTENT fit" question entirely on the theory that
// tdStyle's overflowWrap already absorbed it — it does not, absorbing it just means "wraps without
// overflowing the cell," not "wraps only between words." At the resulting widths, ordinary words in
// realistic content broke MID-WORD ("Netzb|etreib|er", "Dienstl|eister",
// "Energieversorgu|ng Nord AG" — screenshotted eyeball). Pass 3's bar restores the content check pass
// 1 had, but narrower than pass 1's blanket per-column floor: a column's floor is
// max(header-alone need, widest REALISTIC ordinary word in that column) — not "the widest full
// value," since a full organisation name or address list is expected to wrap across several lines
// (that's still fine); only a single WORD breaking partway through is not. "Ordinary word" excludes a
// domain/e-mail matcher, which is allowed to keep wrapping at its own hyphen/dot/@ (that's
// tdStyle's job, see its own comment) — so the Domains / Adressen floor stays header-only.
// Both the header-alone widths and the widest-word content widths below are real, direct
// measurements (unconstrained nowrap spans, Poppins, no table-layout influence) — same font pass 2
// used for headers (thStyle: weight 500, 13px) and tdStyle's own font for content words (weight 400,
// 13px, since plain cell text carries no extra weight):
//   column               header-alone floor   widest ordinary word measured        word-driven floor   floor used
//   Organisation         125 ("Organisation")  "Energieversorgung" 122.3px -> 162    162 (content wins)
//   Kurzname              106 ("Kurzname")     "Stadtwerke" 73.0px -> 112            112 (content wins)
//   Art                    58 ("Art")          "Netzbetreiber" 87.2px -> 127         127 (content wins;
//                                               the widest of the five served sender_kind faces this
//                                               tenant ships — Netzbetreiber/Dienstleister/
//                                               Lieferant/Intern/wMSB)
//   Domains / Adressen   173 ("Domains / Adressen") — n/a, content wraps at -/./@    173 (header only)
// (word-driven floor = ceil(word px) + thStyle's 24px horizontal padding + the same flat 15px
// fallback-font margin ACTIONS_COLUMN_WIDTH's own comment uses, e.g. Organisation: ceil(122.3 + 24 +
// 15) = 162). Declared percentages re-derived proportional to these four floors (162:112:127:173,
// total 574), rounded to whole percent so they sum to 100: Organisation 28%, Kurzname 20%, Art 22%,
// Domains / Adressen 30%.
//
// Fix loop pass 3, follow-up (controller eyeball of the pass-3-first-cut render): that cut's
// TABLE_MIN_WIDTH (1119px) exceeded the measured 1072px card, so `sender-table-scroll` triggered a
// horizontal scrollbar on an ordinary desktop whose only hidden content was the Aktionen cell's own
// trailing padding/slack — a visible artifact with nothing real behind it, not an acceptable trade.
// Two corrections bring the sum back under 1072px without touching the four content-safety floors
// above (which stay exactly as measured — the word-safety requirement does not move):
//   1. ACTIONS_COLUMN_WIDTH's own formula had DOUBLE-COUNTED the cell's 24px padding (added once as
//      a formula term, then again for real by the browser's box model) — see that constant's own
//      comment for the corrected 333 (was 357).
//   2. STATUS_COL_WIDTH was still pass-1's 140, sized well before this pass ever measured what the
//      column actually holds: a status dot (8px) + 6px gap + the widest registry_status face this
//      tenant serves ("Verworfen", the rejected face) — measured directly at 8 + 6 + 73.7 ≈ 87.7px.
//      Re-floored the same way as ACTIONS_COLUMN_WIDTH: ceil(87.7) + the same flat 16px fallback-font
//      margin = 104 (was 140); no separate padding term, same reasoning.
// New sum: 162+112+127+173+128+357 = 1059px — 13px UNDER the measured 1072px card at both 1440px and
// 1280px (verified directly: `sender-table-scroll`'s `scrollWidth` equals its `clientWidth`, i.e. NO
// overflow, at both viewports). Every header still renders on one line, no realistic ordinary word
// breaks mid-word, every registry_status face fits its pill on one line, and all three of the
// proposed row's review-action buttons stay fully visible — none of the four content-safety floors
// changed, only the two columns that had real, uncounted-for slack in them.
const ORGANISATION_COL_FLOOR = 162;
const KURZNAME_COL_FLOOR = 112;
const ART_COL_FLOOR = 127;
const DOMAINS_COL_FLOOR = 173;
const STATUS_COL_WIDTH = 104;
const STATUS_COL_ACTUAL_WIDTH = 128; // STATUS_COL_WIDTH + thStyle's 24px horizontal padding (content-box).
const ACTIONS_COL_ACTUAL_WIDTH = 357; // ACTIONS_COLUMN_WIDTH + thStyle's 24px horizontal padding (content-box).

// The table's minWidth: the sum of every column's REAL rendered width (the four measured percentage
// floors above, plus the two fixed-px columns' own padding-inclusive actual width) — never the bare
// declared width, which is exactly what undercounted this table before (see the comment above). Once
// the card is narrower than this, `sender-table-scroll` scrolls the table instead of squeezing a
// column below what it actually needs. Exported (and pinned by a test) so a future edit to one
// floor can't silently shrink the sum.
export const TABLE_MIN_WIDTH =
  ORGANISATION_COL_FLOOR + KURZNAME_COL_FLOOR + ART_COL_FLOOR + DOMAINS_COL_FLOOR
  + STATUS_COL_ACTUAL_WIDTH + ACTIONS_COL_ACTUAL_WIDTH;

function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: theme.borderRadius.sm, fontSize: 13,
    background: isError ? '#fbe9e9' : '#e9f6ec', color: isError ? theme.colors.error : '#1f7a3d',
  };
}

const INTERN_KIND = 'intern';

// The ONE prose for the protected intern row — the disabled Löschen button's title AND the message
// the server's own 409 backstop produces, so a clerk who reaches the second one never reads a
// different sentence about the same rule.
const INTERN_PROTECTED_PROSE = 'Interne Organisation kann nicht gelöscht werden';

// ─── senderErrorMessage ────────────────────────────────────────────────────────────────────────
// <purpose>Final review, item 1 — core answers this surface's write routes with a JSON error CODE
// and no `message` field (`{error:'matcher_conflict', organisationId}`,
// `{error:'intern_row_protected'}`, `{error:'schema_unavailable', reason}`). formatBackendError
// reads only a string body or `data.message`, so every one of those landed as the bare status prose
// („Konflikt. Bitte Eingabe anpassen.") — true, and useless: it names neither the organisation that
// already owns the domain nor the reason the schema is missing.</purpose>
// <constraints>Codes this surface KNOWS get a German sentence naming the fact; everything else —
// 400 on a self-merge, 404 on an unknown merge target, 401/403/5xx, a network failure — falls
// straight through to formatBackendError, which already answers those correctly. The composed
// sentences go through formatError, the same {action} + {cause} + {remedy} template the fallback
// uses, so the registry's messages read in one voice with the rest of the app rather than as a
// second error dialect. `rows` is the LOADED roster: a matcher_conflict names the owning
// organisation when the roster holds it (the normal case — it is on screen) and falls back to the
// raw id when it does not (a row created in another tab since this list loaded).</constraints>
const SCHEMA_UNAVAILABLE_CAUSE: Record<string, string> = {
  role_unresolvable: 'Die Rolle dieses Mandanten ist nicht auflösbar',
  schema_row_absent: 'Für diesen Mandanten ist kein Schema hinterlegt',
  schema_unreadable: 'Das hinterlegte Schema ist nicht lesbar',
};

export function senderErrorMessage(e: any, action: string, rows: SenderOrganisationRow[]): string {
  const data = e?.response?.data;
  switch (data?.error) {
    case 'matcher_conflict': {
      const owner = rows.find(r => r.id === data.organisationId)?.name ?? data.organisationId;
      return formatError({
        action,
        cause: owner
          ? `Eine Domain oder Adresse ist bereits der Organisation „${owner}" zugeordnet`
          : 'Eine Domain oder Adresse ist bereits einer anderen Organisation zugeordnet',
        remedy: 'Bitte den Eintrag entfernen oder die bestehende Organisation bearbeiten',
      });
    }
    case 'intern_row_protected':
      return formatError({ action, cause: INTERN_PROTECTED_PROSE });
    case 'schema_unavailable':
      return formatError({
        action,
        cause: SCHEMA_UNAVAILABLE_CAUSE[data.reason] ?? 'Das Schema ist nicht verfügbar',
        remedy: 'Bitte die Schema-Konfiguration prüfen',
      });
    default:
      return formatBackendError(e, action);
  }
}

// Status → dot COLOUR only (the word is a served face, see StatusCell below). Not a `kind` label
// map (that stays fully vocabulary-driven, see `faceFor` below) — the registry's own review
// lifecycle, the same four states SenderOrganisationRef (types.ts) already carries, spelled here
// for the ONE surface that reviews them.
const STATUS_DOT_COLOR: Record<SenderOrganisationRow['status'], string> = {
  proposed: theme.colors.suggestion,
  seeded: theme.colors.success,
  confirmed: theme.colors.success,
  rejected: theme.colors.borderLight,
};
// CONTROLLER RULING (fix round 1): `rejected` reads „Verworfen", not „Abgelehnt" — a rejected
// registry proposal keeps its matchers so the same domain is never re-proposed (spec §3.5), i.e. it
// is a DISCARDED proposal, not a refused request, and „Verworfen" is the correct register for that.
// The WORD is a served face now (P4b, registry_status family) — this ruling lives on as the
// standard fixture's own served wording, not as a code-owned literal.
// Fix loop pass 4: a tenant can re-word this served face to anything, including a single long
// unbroken compound word ("Freigabeausstehend", ~134px — no space, so nothing for ordinary
// word-wrap to break on) that overflows this ~90px-wide cell into Aktionen. `tdStyle`'s
// `overflow-wrap: break-word` (on the surrounding <td>) does not help: this word sits inside an
// `inline-flex` wrapper (the dot+word span above), and per the flexbox spec a flex item's AUTOMATIC
// MINIMUM SIZE is based on its content's min-content size — for text that is normally the size of
// its longest unbreakable run, and `break-word` does NOT change that automatic-minimum calculation
// (only `overflow-wrap: anywhere` does — verified directly in headless Chromium: the bare word
// overflowed its cell, `scrollWidth` 160px against a 128px cell; wrapping it in its own span with
// `overflow-wrap: anywhere` + `minWidth: 0` brought `scrollWidth` back down to the cell's own
// 128px, zero overflow). Scoped to THIS span only, not `tdStyle` — the surrounding cell's other
// content (nothing else lives in Status) never needed `anywhere`'s more aggressive break, and this
// is the one word here that sits inside a flex item rather than ordinary block flow.
const StatusCell: React.FC<{ status: SenderOrganisationRow['status'] }> = ({ status }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        backgroundColor: STATUS_DOT_COLOR[status],
      }}
    />
    <ServedFace
      family="registry_status" faceKey={status} width={64} height={13}
      render={word => <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{word}</span>}
    />
  </span>
);

// One matcher per line, trimmed + LOWERCASED — spec §3 pins `sender_matchers.value` lowercase, and
// this is the one seam that turns whatever case an admin typed into that stored shape (mirrors
// RoutingVerwaltung's rule_id posture: a client-side shape check ahead of the server, which stays
// authoritative). A bare domain (`beispiel.de`) is a `domain` matcher; a single `@` whose RIGHT side
// is itself a valid domain (`max@beispiel.de`) is an `address` matcher. Empty lines are dropped
// silently; anything else (no `@` and not a domain shape, more than one `@`, or an `@` whose domain
// side doesn't match) is INVALID and blocks the whole submission — surfaced by name, not silently
// dropped, so a typo can't quietly vanish from the saved roster.
const MATCHER_DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/;

type MatcherParseResult =
  | { ok: true; matchers: SenderMatcher[] }
  | { ok: false; invalidValue: string };

function classifyMatcherValue(value: string): SenderMatcher['type'] | null {
  const atIndex = value.indexOf('@');
  if (atIndex === -1) {
    return MATCHER_DOMAIN_RE.test(value) ? 'domain' : null;
  }
  // Exactly one '@', not leading, and the part after it is itself a valid domain.
  if (atIndex === 0 || value.indexOf('@', atIndex + 1) !== -1) return null;
  return MATCHER_DOMAIN_RE.test(value.slice(atIndex + 1)) ? 'address' : null;
}

function parseMatcherLines(text: string): MatcherParseResult {
  const matchers: SenderMatcher[] = [];
  for (const raw of text.split('\n')) {
    const value = raw.trim().toLowerCase();
    if (!value) continue;
    const type = classifyMatcherValue(value);
    if (!type) return { ok: false, invalidValue: value };
    matchers.push({ type, value });
  }
  return { ok: true, matchers };
}
const matchersToLines = (m: SenderMatcher[]): string => (m ?? []).map(x => x.value).join('\n');

type FormMode = 'create' | 'edit' | 'merge';
type FormState = { name: string; shortName: string; kind: string; matchersText: string };
const emptyForm: FormState = { name: '', shortName: '', kind: '', matchersText: '' };

// The Kurzname's bound, mirrored from core's own check (`shortName` is 1-40 after trimming, or
// absent). Mirrored, not guessed: the server stays authoritative and answers 400, but an admin
// should learn the limit while typing rather than after saving — the same posture the matcher
// parser above takes.
const SHORT_NAME_MAX = 40;

// Blank (or whitespace) means „no Kurzname" — the registry row then has no face of its own and the
// list row's pill falls back to the full name. Sent as an explicit null rather than omitted: on a
// PATCH an absent field leaves the stored value standing, and an admin who cleared the input has
// decided the opposite.
const shortNameForWire = (value: string): string | null => value.trim() || null;

const AbsenderVerwaltung: React.FC = () => {
  const cm = useCategoryMeta();
  const senderKindAttr = cm.attribute('sender_kind');
  const kindValues = senderKindAttr?.values ?? [];
  const faceFor = (kind: string) => cm.attributeFace('sender_kind', kind);

  const [rows, setRows] = useState<SenderOrganisationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Per-row busy flag for the one-click review actions (Bestätigen/Ablehnen) — distinct from
  // `deletingId` below, which belongs to the confirm-then-call Löschen flow.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setRows(await listSenderOrganisations());
    } catch (e) {
      setError(formatBackendError(e, 'Laden der Absender-Organisationen'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Proposed rows first — a stable sort, so ties (every non-proposed row) keep the server's own
  // order rather than being re-shuffled on every reload.
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.status === 'proposed' ? 0 : 1) - (b.status === 'proposed' ? 0 : 1)),
    [rows],
  );

  const activeRow = activeId ? rows.find(r => r.id === activeId) ?? null : null;

  const resetForm = () => {
    setForm(emptyForm); setFormMode('create'); setActiveId(null); setMergeTarget('');
    setSaveError(''); setSaveSuccess('');
  };

  const startEdit = (r: SenderOrganisationRow) => {
    setFormMode('edit'); setActiveId(r.id);
    setForm({ name: r.name, shortName: r.shortName ?? '', kind: r.kind, matchersText: matchersToLines(r.matchers) });
    setSaveError(''); setSaveSuccess('');
  };

  const startMerge = (r: SenderOrganisationRow) => {
    setFormMode('merge'); setActiveId(r.id);
    setMergeTarget('');
    setSaveError(''); setSaveSuccess('');
  };

  // Parsed once per matchersText change — both validationError (below) and handleSubmit read the
  // SAME parse, so a value judged valid at submit time is exactly the value that gets sent.
  const matcherParse = useMemo(() => parseMatcherLines(form.matchersText), [form.matchersText]);

  const validationError = useMemo(() => {
    if (formMode === 'merge') return mergeTarget ? '' : 'Ziel-Organisation ist erforderlich.';
    if (!form.name.trim()) return 'Name ist erforderlich.';
    if (!form.kind.trim()) return 'Art ist erforderlich.';
    if (form.shortName.trim().length > SHORT_NAME_MAX) return `Kurzname darf höchstens ${SHORT_NAME_MAX} Zeichen lang sein.`;
    if (!matcherParse.ok) return `Ungültiger Eintrag: ${matcherParse.invalidValue} — Domain wie beispiel.de oder Adresse wie name@beispiel.de`;
    if (matcherParse.matchers.length === 0) return 'Mindestens ein Domain- oder Adress-Eintrag ist erforderlich.';
    return '';
  }, [form, formMode, mergeTarget, matcherParse]);

  const handleConfirm = async (id: string) => {
    setBusyId(id); setError(''); setSuccess('');
    try {
      await confirmSenderOrganisation(id);
      setSuccess('Organisation bestätigt.');
      await load();
    } catch (e) {
      setError(senderErrorMessage(e, 'Bestätigen der Organisation', rows));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id: string) => {
    setBusyId(id); setError(''); setSuccess('');
    try {
      await rejectSenderOrganisation(id);
      // Mirrors the served registry_status.rejected face ('Verworfen' on the standard fixture) —
      // the toast and the badge must name the same resulting state.
      setSuccess('Organisation verworfen.');
      await load();
    } catch (e) {
      setError(senderErrorMessage(e, 'Ablehnen der Organisation', rows));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id); setError(''); setSuccess('');
    try {
      await deleteSenderOrganisation(id);
      setSuccess('Organisation gelöscht.');
      setDeleteConfirm(null);
      if (activeId === id) resetForm();
      await load();
    } catch (e) {
      setError(senderErrorMessage(e, 'Löschen der Organisation', rows));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async () => {
    if (validationError) { setSaveError(validationError); return; }
    setSaving(true); setSaveError(''); setSaveSuccess('');
    try {
      if (formMode === 'merge') {
        if (!activeId) return;
        const mergedName = activeRow?.name ?? '';
        const result = await mergeSenderOrganisation(activeId, mergeTarget);
        resetForm();
        setSaveSuccess(`„${mergedName}" zusammengeführt — ${result.ticketsRepointed} Ticket(s) umgehängt.`);
      } else {
        // validationError already blocked submission for an unparseable line — matcherParse.ok is
        // guaranteed true here.
        const payload: SenderOrganisationUpsert = {
          name: form.name.trim(), shortName: shortNameForWire(form.shortName), kind: form.kind.trim(),
          matchers: matcherParse.ok ? matcherParse.matchers : [],
        };
        if (formMode === 'edit' && activeId) {
          await updateSenderOrganisation(activeId, payload);
        } else {
          await createSenderOrganisation(payload);
        }
        const message = formMode === 'edit' ? `Organisation „${payload.name}" aktualisiert.` : `Organisation „${payload.name}" angelegt.`;
        // resetForm() clears saveSuccess along with the form — set the success message AFTER so it
        // isn't clobbered by that clear (both calls land in the same tick/batch — RoutingVerwaltung
        // precedent).
        resetForm();
        setSaveSuccess(message);
      }
      await load();
    } catch (e) {
      setSaveError(senderErrorMessage(e, formMode === 'merge' ? 'Zusammenführen der Organisation' : formMode === 'edit' ? 'Aktualisieren der Organisation' : 'Anlegen der Organisation', rows));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BentoCard title="Absender">
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: 0, lineHeight: 1.5 }}>
          Die Absender-Registrierung ordnet eingehende Absenderadressen automatisch einer
          Organisation zu. Vorgeschlagene Zuordnungen hier prüfen und bestätigen, mit einer
          bestehenden Organisation zusammenführen oder ablehnen; bestehende Organisationen lassen
          sich bearbeiten oder löschen.
        </p>

        {/* Fixed-height, always-rendered — same layout-stability idiom as RoutingVerwaltung.
            `role="status"` (final review, item 7) makes it a polite live region: the slot is
            already in the DOM and already sized, so announcing what lands in it shifts nothing. */}
        <div role="status" data-testid="sender-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch' }}>
          {error ? (
            <div style={{ ...feedbackStyle(true), width: '100%', boxSizing: 'border-box' }}>{error}</div>
          ) : success ? (
            <div style={{ ...feedbackStyle(false), width: '100%', boxSizing: 'border-box' }}>{success}</div>
          ) : null}
        </div>

        {/* `tableLayout: 'fixed'` + a width on EVERY column (final review, item 3). With the
            browser's default auto layout the column widths are computed from the CONTENT, so the
            single full-width „Lade Organisationen…" cell sized the header one way and the real rows
            — organisation names, matcher lists — sized it another: the five header cells visibly
            jumped the moment the data landed (UI Layout Stability). Fixed layout reads the widths
            off this header row alone, so the frame is identical before and after the fetch and on
            every subsequent reload, whatever the rows contain.

            The wrapper below (final review) is what makes that fixed frame safe on a narrow card:
            without it, a card narrower than TABLE_MIN_WIDTH would force the browser to squeeze every
            fixed/percentage column below the floor its own header (or the Aktionen column's widest
            action set) needs — which is how the Aktionen column clipped its third button in the
            first place. `overflowX: 'auto'` plus the table's own `minWidth` means the CARD never
            squeezes: the TABLE scrolls inside it instead. */}
        <div data-testid="sender-table-scroll" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: TABLE_MIN_WIDTH, borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.colors.bgLight}` }}>
              <th style={{ ...thStyle, width: '28%' }}>Organisation</th>
              {/* The Kurzname column sits directly beside the name it abbreviates — the two are one
                  fact at two lengths, and an admin reviewing the roster reads them together. */}
              <th style={{ ...thStyle, width: '20%' }}>Kurzname</th>
              <th style={{ ...thStyle, width: '22%' }}>Art</th>
              <th style={{ ...thStyle, width: '30%' }}>Domains / Adressen</th>
              <th style={{ ...thStyle, width: STATUS_COL_WIDTH }}>Status</th>
              <th style={{ ...thStyle, width: ACTIONS_COLUMN_WIDTH, whiteSpace: 'nowrap' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: '24px 12px', textAlign: 'center' }}>
                <Spinner size={22} label="Lade Organisationen…" />
              </td></tr>
            )}
            {!loading && sortedRows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '20px 12px', color: theme.colors.textSecondary, textAlign: 'center' }}>
                Noch keine Absender-Organisation angelegt.
              </td></tr>
            )}
            {!loading && sortedRows.map(r => {
              const face = faceFor(r.kind);
              const isIntern = r.kind === INTERN_KIND;
              return (
                <tr key={r.id} style={{ borderBottom: `1px solid ${theme.colors.bgLight}` }}>
                  <td style={tdStyle}>{r.name}</td>
                  {/* „—" rather than an empty cell: no Kurzname is a STATE (the row's pill wears
                      the full name), and an empty cell reads as a rendering that failed. */}
                  <td style={tdStyle}>{r.shortName || '—'}</td>
                  <td style={tdStyle}>
                    {face === null
                      ? <span style={{ color: theme.colors.textSecondary }}>{attributeValueStandIn(r.kind)}</span>
                      : face}
                  </td>
                  <td style={tdStyle}>{r.matchers.map(m => m.value).join(', ') || '—'}</td>
                  <td style={tdStyle}><StatusCell status={r.status} /></td>
                  <td style={{ ...tdStyle, width: ACTIONS_COLUMN_WIDTH, whiteSpace: 'nowrap' }}>
                    {r.status === 'proposed' ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleConfirm(r.id)} style={btnStyle('primary')} disabled={busyId === r.id}>
                          Bestätigen
                        </button>
                        <button onClick={() => startMerge(r)} style={btnStyle('ghost')} disabled={busyId === r.id}>
                          Zusammenführen
                        </button>
                        <button onClick={() => handleReject(r.id)} style={btnStyle('ghost')} disabled={busyId === r.id}>
                          Ablehnen
                        </button>
                      </div>
                    ) : deleteConfirm === r.id ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => handleDelete(r.id)} style={btnStyle('danger')} disabled={deletingId === r.id}>
                          {deletingId === r.id ? 'Löscht…' : 'Löschen'}
                        </button>
                        <button onClick={() => setDeleteConfirm(null)} style={btnStyle('ghost')}>Abbrechen</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => startEdit(r)} style={btnStyle('ghost')}>Bearbeiten</button>
                        <button
                          onClick={() => setDeleteConfirm(r.id)}
                          style={{ ...btnStyle('ghost'), opacity: isIntern ? 0.5 : 1, cursor: isIntern ? 'default' : 'pointer' }}
                          disabled={isIntern}
                          title={isIntern ? INTERN_PROTECTED_PROSE : undefined}
                        >Löschen</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* ── Formular: eigener Abschnitt unter der Tabelle, damit die Tabelle nie springt. ── */}
        <div data-testid="sender-form-section" style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md, borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>
            {formMode === 'merge'
              ? `Zusammenführen: ${activeRow?.name ?? ''} → …`
              : formMode === 'edit'
                ? `Organisation bearbeiten: ${activeRow?.name ?? form.name}`
                : 'Neue Organisation anlegen'}
          </span>

          {formMode === 'merge' ? (
            <div>
              <label htmlFor="sender-merge-target" style={labelStyle}>Ziel-Organisation</label>
              <select
                id="sender-merge-target" style={inputStyle}
                value={mergeTarget} onChange={ev => setMergeTarget(ev.target.value)}
              >
                <option value="">— auswählen —</option>
                {rows.filter(r => r.id !== activeId).map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: theme.spacing.md }}>
                <div>
                  <label htmlFor="sender-name" style={labelStyle}>Name</label>
                  <input id="sender-name" style={inputStyle} value={form.name}
                    placeholder="z.B. Stadtwerke Nord"
                    onChange={ev => setForm(f => ({ ...f, name: ev.target.value }))} />
                </div>
                <div>
                  <label htmlFor="sender-short-name" style={labelStyle}>Kurzname</label>
                  <input id="sender-short-name" style={inputStyle} value={form.shortName}
                    maxLength={SHORT_NAME_MAX}
                    placeholder="z.B. SWN"
                    onChange={ev => setForm(f => ({ ...f, shortName: ev.target.value }))} />
                </div>
                <div>
                  <label htmlFor="sender-kind" style={labelStyle}>Art</label>
                  <select
                    id="sender-kind" style={inputStyle}
                    value={form.kind} onChange={ev => setForm(f => ({ ...f, kind: ev.target.value }))}
                  >
                    <option value="">— auswählen —</option>
                    {kindValues.map(v => {
                      const kindFace = faceFor(v);
                      return (
                        <option key={v} value={v} style={kindFace === null ? { color: theme.colors.textSecondary } : undefined}>
                          {kindFace === null ? attributeValueStandIn(v) : kindFace}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="sender-matchers" style={labelStyle}>Domains / Adressen (ein Eintrag pro Zeile)</label>
                <textarea id="sender-matchers" style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  value={form.matchersText} placeholder={'beispiel.de\nmax@beispiel.de'}
                  onChange={ev => setForm(f => ({ ...f, matchersText: ev.target.value }))} />
              </div>
            </>
          )}

          {/* Pre-allocated, in-view by construction — same idiom as RoutingVerwaltung's
              routing-save-feedback. */}
          <div role="status" data-testid="sender-save-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch' }}>
            {saveError ? (
              <div style={{ ...feedbackStyle(true), width: '100%', boxSizing: 'border-box', animation: 'stx-fadeIn 0.2s ease' }}>{saveError}</div>
            ) : saveSuccess ? (
              <div style={{ ...feedbackStyle(false), width: '100%', boxSizing: 'border-box', animation: 'stx-fadeIn 0.2s ease' }}>{saveSuccess}</div>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
            <div style={{ flex: 1 }} />
            {formMode !== 'create' && <button onClick={resetForm} style={btnStyle('ghost')}>Abbrechen</button>}
            <button onClick={handleSubmit} style={btnStyle('primary')} disabled={saving}>
              {formMode === 'merge'
                ? (saving ? 'Führt zusammen…' : 'Zusammenführen')
                : formMode === 'edit'
                  ? (saving ? 'Speichern…' : 'Organisation aktualisieren')
                  : (saving ? 'Speichern…' : 'Organisation anlegen')}
            </button>
          </div>
        </div>
      </div>
    </BentoCard>
  );
};

export default AbsenderVerwaltung;
